import PropTypes from 'prop-types';

import { Box, Button } from '@mui/material';

import SortableTableComponent from '../../Table/SortableTableComponent';
import ApprovalStatusComponent from './ApprovalStatusComponent';

import { checkHasPermissions, formatDate } from '../../../utils';

import { TABLE_ID } from '../../Table/constants';
import { ALL_COMMENTS, PENDING_COMMENTS } from './constants';

import permissions from '../../../permissions';
import { TABLE_DATA_TYPES } from '../../../staticData';
import StyledExpanderLine from '../../Forms/StyledExpanderLine';
import CellPersonWithImage from '../../Table/CellPersonWithImage';
import Tooltip from '../../Tooltip';

const CommentApprovalComponent = ({
  commentsRequireApproval,
  comments,
  updateCommentStatus,
  removeComment,
  setStatus,
  isShowAllComments,
}) => {
  if (!commentsRequireApproval && !isShowAllComments)
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          my: '2rem',
          ml: '1rem',
        }}
      >
        Currently there are no pending approval comments.{' '}
        <Button sx={{ ml: '-1rem' }} onClick={() => setStatus(ALL_COMMENTS)}>
          Show all comments
        </Button>
      </Box>
    );

  const columns = [
    {
      Header: 'Comment',
      id: 'text',
      accessor: ({ text }) => (
        <Tooltip placement="top" trigger="hover" tooltip={text}>
          {text}
        </Tooltip>
      ),
      sortable: false,
    },
    {
      Header: 'Commented on',
      id: 'created_at',
      headerClassName: 'sort',
      width: 180,
      accessor: ({ createdAt }) => formatDate(createdAt),
    },
    {
      Header: 'Commented by',
      id: 'creator',
      headerClassName: 'sort',
      width: 200,
      accessor: ({ creator }) => (
        <CellPersonWithImage
          person={creator}
          linkUrl={`/global-person-search/${creator?.id}`}
        />
      ),
    },
    {
      Header: 'Status',
      id: 'approval_status',
      headerClassName: 'sort',
      width: 150,
      show: checkHasPermissions(permissions.approve_community_post),
      accessor: ({ id, approvalStatus }) => (
        <ApprovalStatusComponent
          id={id}
          approvalStatus={approvalStatus}
          onUpdate={updateCommentStatus}
          onRemove={removeComment}
        />
      ),
    },
  ];

  return (
    <Box sx={{ mt: '2rem', mb: '5rem' }}>
      <Button
        onClick={() =>
          setStatus(isShowAllComments ? PENDING_COMMENTS : ALL_COMMENTS)
        }
      >
        {isShowAllComments
          ? 'Show only pending approval comments'
          : 'Show all comments'}
      </Button>
      <StyledExpanderLine>
        <SortableTableComponent
          tableId={TABLE_ID.communityComments}
          columns={columns}
          entries={comments}
          skeletonInitialEntries={5}
          tableDataType={
            isShowAllComments
              ? TABLE_DATA_TYPES.comments
              : TABLE_DATA_TYPES.pendingApprovalComments
          }
        />
      </StyledExpanderLine>
    </Box>
  );
};

CommentApprovalComponent.propTypes = {
  commentsRequireApproval: PropTypes.bool,
  isShowAllComments: PropTypes.bool.isRequired,
  comments: PropTypes.instanceOf(Array),
  updateCommentStatus: PropTypes.func.isRequired,
  removeComment: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
};

export default CommentApprovalComponent;
